/**
 * DialogHTML - HTML overlay dialog system for NPC conversations.
 * Replaces the canvas-based DialogUI with a DOM-based approach.
 * CSS injected once, DOM built on open(), removed on close().
 */

import { NPC, NPCType, Dialog, QuestDialogAction } from '@client/entities/NPC';
import { getAudioManager } from '@client/audio/AudioManager';
import { t } from '@locales/index';

// ========================
// NPC portrait config
// ========================

const NPC_PORTRAIT_INFO: Record<NPCType, { color: string; darkColor: string; label: string }> = {
  kam:      { color: '#9b59b6', darkColor: '#5b2d7a', label: 'Kam' },
  demirci:  { color: '#e67e22', darkColor: '#8a4c14', label: 'Demirci' },
  tacir:    { color: '#f1c40f', darkColor: '#917608', label: 'Tacir' },
  ihtiyar:  { color: '#ecf0f1', darkColor: '#8e9091', label: 'Ihtiyar' },
  otaci:    { color: '#2ecc71', darkColor: '#1a7a43', label: 'Otaci' },
  muhafiz:  { color: '#3498db', darkColor: '#1e5b83', label: 'Muhafiz' },
};

// ========================
// CSS (injected once)
// ========================

const CSS = `
/* Backdrop */
.dialog-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 200;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding-bottom: 50px;
  font-family: 'Segoe UI', Tahoma, sans-serif;
  color: #f4e8d4;
  user-select: none;
  animation: dialog-fade-in 0.2s ease-out;
}

@keyframes dialog-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Main dialog box */
.dialog-box {
  position: relative;
  max-width: 650px;
  width: 90vw;
  background: linear-gradient(180deg, rgba(28, 24, 38, 0.97) 0%, rgba(16, 14, 22, 0.98) 100%);
  border: 2px solid #4a4a6a;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: row;
  padding: 0;
  overflow: visible;
  animation: dialog-slide-up 0.25s ease-out;
}

@keyframes dialog-slide-up {
  from { transform: translateY(20px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

/* Portrait area */
.dialog-portrait {
  flex-shrink: 0;
  width: 80px;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px 0 20px 16px;
}

.dialog-portrait-circle {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  border: 2px solid #ffd700;
  box-shadow: 0 0 12px rgba(255, 215, 0, 0.25), inset 0 -8px 16px rgba(0, 0, 0, 0.3);
  position: relative;
  overflow: hidden;
}

/* Content area */
.dialog-content {
  flex: 1;
  padding: 16px 20px 16px 14px;
  display: flex;
  flex-direction: column;
  min-height: 140px;
}

/* NPC name */
.dialog-npc-name {
  font-size: 16px;
  font-weight: 700;
  color: #ffd700;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  margin-bottom: 1px;
  letter-spacing: 0.3px;
}

/* NPC title */
.dialog-npc-title {
  font-size: 11px;
  color: #8a8a9a;
  margin-bottom: 10px;
  font-style: italic;
}

/* Dialog text */
.dialog-text {
  font-size: 14px;
  line-height: 1.55;
  color: #e8dcc8;
  flex: 1;
  margin-bottom: 10px;
  min-height: 40px;
}

/* Typewriter cursor */
.dialog-text .typewriter-cursor {
  display: inline-block;
  width: 2px;
  height: 14px;
  background: #ffd700;
  margin-left: 2px;
  vertical-align: text-bottom;
  animation: cursor-blink 0.6s step-end infinite;
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50%      { opacity: 0; }
}

/* Options list */
.dialog-options {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: auto;
}

.dialog-option {
  display: flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  font-size: 13px;
  color: #c8c0b0;
}

.dialog-option:hover,
.dialog-option--selected {
  background: rgba(100, 100, 140, 0.45);
  color: #ffffff;
}

.dialog-option--selected .dialog-option-num {
  color: #ffd700;
}

.dialog-option-num {
  font-size: 11px;
  font-weight: 700;
  color: #6a6a7a;
  margin-right: 8px;
  min-width: 20px;
  text-align: center;
  background: rgba(255, 255, 255, 0.04);
  padding: 2px 5px;
  border-radius: 3px;
  transition: color 0.12s;
}

.dialog-option:hover .dialog-option-num {
  color: #ffd700;
}

.dialog-option-text {
  flex: 1;
}

.dialog-option--selected .dialog-option-text {
  font-weight: 600;
}

/* Continue hint */
.dialog-hint {
  font-size: 11px;
  color: #6a6a7a;
  text-align: right;
  margin-top: 6px;
}

/* Separator line above options */
.dialog-separator {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 215, 0, 0.15), transparent);
  margin: 6px 0 8px;
}
`;

// ========================
// CSS injection
// ========================

let cssInjected = false;
function injectCSS(): void {
  if (cssInjected) return;
  const style = document.createElement('style');
  style.textContent = CSS;
  document.head.appendChild(style);
  cssInjected = true;
}

// ========================
// DialogHTML class
// ========================

export class DialogHTML {
  private currentNPC: NPC | null = null;
  private currentDialog: Dialog | null = null;
  private selectedOption: number = 0;
  private open_: boolean = false;

  // DOM elements
  private backdrop: HTMLDivElement | null = null;
  private textEl: HTMLDivElement | null = null;
  private optionsEl: HTMLDivElement | null = null;

  // Typewriter
  private typewriterTimer: number = 0;
  private typewriterIndex: number = 0;
  private fullText: string = '';
  private typewriterDone: boolean = false;
  private readonly typewriterSpeed: number = 25; // ms per character

  // Callbacks
  private onCloseCallback: (() => void) | null = null;
  private onShopOpenCallback: ((npc: NPC) => void) | null = null;
  private onQuestActionCallback: ((action: QuestDialogAction) => void) | null = null;

  // Bound handlers for cleanup
  private boundKeyDown: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    injectCSS();
  }

  // ---- Public API ----

  open(npc: NPC, dialogId?: string): void {
    this.currentNPC = npc;
    this.currentDialog = npc.getDialog(dialogId);
    if (!this.currentDialog) return;
    this.selectedOption = 0;
    this.buildDOM();
    this.open_ = true;
    getAudioManager().play('ui_open');
  }

  openWithDialog(npc: NPC, dialog: Dialog): void {
    this.currentNPC = npc;
    this.currentDialog = dialog;
    this.selectedOption = 0;
    this.buildDOM();
    this.open_ = true;
  }

  close(): void {
    this.destroyDOM();
    this.currentNPC = null;
    this.currentDialog = null;
    this.open_ = false;
    getAudioManager().play('ui_close');

    if (this.onCloseCallback) {
      this.onCloseCallback();
    }
  }

  isVisible(): boolean {
    return this.open_;
  }

  setOnClose(cb: () => void): void {
    this.onCloseCallback = cb;
  }

  setOnShopOpen(cb: (npc: NPC) => void): void {
    this.onShopOpenCallback = cb;
  }

  setOnQuestAction(cb: (action: QuestDialogAction) => void): void {
    this.onQuestActionCallback = cb;
  }

  // ---- DOM building ----

  private buildDOM(): void {
    // Clean up any existing
    this.destroyDOM();

    if (!this.currentNPC || !this.currentDialog) return;

    const npc = this.currentNPC;
    const dialog = this.currentDialog;
    const info = NPC_PORTRAIT_INFO[npc.type];

    // Backdrop
    const backdrop = document.createElement('div');
    backdrop.className = 'dialog-backdrop';
    backdrop.addEventListener('click', (e) => {
      if (e.target === backdrop) this.close();
    });

    // Dialog box
    const box = document.createElement('div');
    box.className = 'dialog-box';

    // Portrait
    const portraitWrap = document.createElement('div');
    portraitWrap.className = 'dialog-portrait';

    const portraitCircle = document.createElement('div');
    portraitCircle.className = 'dialog-portrait-circle';
    portraitCircle.style.background = `radial-gradient(circle at 50% 35%, ${info.color}, ${info.darkColor})`;
    portraitCircle.textContent = npc.name.charAt(0).toUpperCase();
    portraitWrap.appendChild(portraitCircle);

    // Content
    const content = document.createElement('div');
    content.className = 'dialog-content';

    // NPC name
    const nameEl = document.createElement('div');
    nameEl.className = 'dialog-npc-name';
    nameEl.textContent = dialog.speaker;

    // NPC title
    const titleEl = document.createElement('div');
    titleEl.className = 'dialog-npc-title';
    titleEl.textContent = npc.title || '';

    // Dialog text
    const textEl = document.createElement('div');
    textEl.className = 'dialog-text';
    this.textEl = textEl;

    // Options container
    const optionsEl = document.createElement('div');
    optionsEl.className = 'dialog-options';
    optionsEl.style.display = 'none';
    this.optionsEl = optionsEl;

    // Assemble
    content.appendChild(nameEl);
    if (npc.title) content.appendChild(titleEl);
    content.appendChild(textEl);
    content.appendChild(optionsEl);

    box.appendChild(portraitWrap);
    box.appendChild(content);
    backdrop.appendChild(box);
    document.body.appendChild(backdrop);
    this.backdrop = backdrop;

    // Start typewriter
    this.startTypewriter(dialog.text);

    // Keyboard handler
    this.boundKeyDown = (e: KeyboardEvent) => this.handleKeyDown(e);
    window.addEventListener('keydown', this.boundKeyDown);
  }

  private destroyDOM(): void {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = 0;
    }
    if (this.boundKeyDown) {
      window.removeEventListener('keydown', this.boundKeyDown);
      this.boundKeyDown = null;
    }
    if (this.backdrop) {
      this.backdrop.remove();
      this.backdrop = null;
    }
    this.textEl = null;
    this.optionsEl = null;
  }

  // ---- Typewriter ----

  private startTypewriter(text: string): void {
    this.fullText = text;
    this.typewriterIndex = 0;
    this.typewriterDone = false;

    if (this.typewriterTimer) clearInterval(this.typewriterTimer);

    this.typewriterTimer = window.setInterval(() => {
      this.typewriterIndex++;
      if (this.typewriterIndex >= this.fullText.length) {
        this.typewriterIndex = this.fullText.length;
        this.finishTypewriter();
      }
      this.updateTextDisplay();
    }, this.typewriterSpeed);

    this.updateTextDisplay();
  }

  private finishTypewriter(): void {
    if (this.typewriterTimer) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = 0;
    }
    this.typewriterDone = true;
    this.typewriterIndex = this.fullText.length;
    this.updateTextDisplay();
    this.showOptions();
  }

  private updateTextDisplay(): void {
    if (!this.textEl) return;
    const revealed = this.fullText.substring(0, this.typewriterIndex);

    if (!this.typewriterDone) {
      this.textEl.innerHTML = this.escapeHTML(revealed) + '<span class="typewriter-cursor"></span>';
    } else {
      this.textEl.textContent = this.fullText;
    }
  }

  // ---- Options rendering ----

  private showOptions(): void {
    if (!this.optionsEl || !this.currentDialog) return;

    const options = this.currentDialog.options || [];
    this.optionsEl.innerHTML = '';

    if (options.length === 0) {
      // Show close hint
      const hint = document.createElement('div');
      hint.className = 'dialog-hint';
      hint.textContent = t('dialog.close');
      this.optionsEl.appendChild(hint);
      this.optionsEl.style.display = 'flex';
      return;
    }

    // Separator
    const sep = document.createElement('div');
    sep.className = 'dialog-separator';
    this.optionsEl.appendChild(sep);

    for (let i = 0; i < options.length; i++) {
      const opt = options[i];
      const row = document.createElement('div');
      row.className = 'dialog-option' + (i === this.selectedOption ? ' dialog-option--selected' : '');
      row.dataset.index = String(i);

      const num = document.createElement('span');
      num.className = 'dialog-option-num';
      num.textContent = `${i + 1}`;

      const text = document.createElement('span');
      text.className = 'dialog-option-text';
      text.textContent = opt.text;

      row.appendChild(num);
      row.appendChild(text);

      row.addEventListener('click', () => {
        this.selectedOption = i;
        this.selectCurrentOption();
      });

      row.addEventListener('mouseenter', () => {
        this.selectedOption = i;
        this.refreshOptionHighlight();
      });

      this.optionsEl.appendChild(row);
    }

    this.optionsEl.style.display = 'flex';
  }

  private refreshOptionHighlight(): void {
    if (!this.optionsEl) return;
    const rows = this.optionsEl.querySelectorAll('.dialog-option');
    rows.forEach((row, i) => {
      row.classList.toggle('dialog-option--selected', i === this.selectedOption);
    });
  }

  // ---- Selection logic ----

  private selectCurrentOption(): void {
    if (!this.currentDialog || !this.currentNPC) return;

    const options = this.currentDialog.options || [];

    // If no options, close
    if (options.length === 0) {
      this.close();
      return;
    }

    // If text not fully revealed, skip to end
    if (!this.typewriterDone) {
      this.finishTypewriter();
      return;
    }

    const option = options[this.selectedOption];

    // Execute action callback
    if (option.action) {
      option.action();
    }

    // Handle quest actions
    if (option.questAction && this.onQuestActionCallback) {
      this.onQuestActionCallback(option.questAction);
    }

    // Check for shop trigger
    if (option.text.toLowerCase().includes('d\u00FCkkan')) {
      if (this.onShopOpenCallback && this.currentNPC) {
        this.onShopOpenCallback(this.currentNPC);
      }
    }

    // Navigate to next dialog or close
    if (option.nextDialogId) {
      const nextDialog = this.currentNPC.getDialog(option.nextDialogId);
      if (nextDialog) {
        this.currentDialog = nextDialog;
        this.selectedOption = 0;
        // Rebuild content in place
        this.rebuildContent();
      } else {
        this.close();
      }
    } else {
      this.close();
    }
  }

  /** Rebuild dialog content without destroying/recreating the whole backdrop */
  private rebuildContent(): void {
    if (!this.currentNPC || !this.currentDialog) return;

    const dialog = this.currentDialog;
    const info = NPC_PORTRAIT_INFO[this.currentNPC.type];

    // Update portrait letter (in case NPC changed, unlikely but safe)
    if (this.backdrop) {
      const circle = this.backdrop.querySelector('.dialog-portrait-circle') as HTMLDivElement | null;
      if (circle) {
        circle.style.background = `radial-gradient(circle at 50% 35%, ${info.color}, ${info.darkColor})`;
        circle.textContent = this.currentNPC.name.charAt(0).toUpperCase();
      }

      const nameEl = this.backdrop.querySelector('.dialog-npc-name') as HTMLDivElement | null;
      if (nameEl) nameEl.textContent = dialog.speaker;
    }

    // Hide options
    if (this.optionsEl) {
      this.optionsEl.style.display = 'none';
      this.optionsEl.innerHTML = '';
    }

    // Restart typewriter
    this.startTypewriter(dialog.text);
  }

  // ---- Keyboard ----

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.open_ || !this.currentDialog) return;

    const key = e.key;
    const options = this.currentDialog.options || [];

    switch (key) {
      case 'ArrowUp':
      case 'w':
      case 'W':
        if (this.typewriterDone && options.length > 0) {
          e.preventDefault();
          this.selectedOption = (this.selectedOption - 1 + options.length) % options.length;
          this.refreshOptionHighlight();
        }
        break;

      case 'ArrowDown':
      case 's':
      case 'S':
        if (this.typewriterDone && options.length > 0) {
          e.preventDefault();
          this.selectedOption = (this.selectedOption + 1) % options.length;
          this.refreshOptionHighlight();
        }
        break;

      case 'Enter':
      case 'e':
      case 'E':
      case ' ':
        e.preventDefault();
        if (!this.typewriterDone) {
          this.finishTypewriter();
        } else {
          this.selectCurrentOption();
        }
        break;

      case 'Escape':
        e.preventDefault();
        this.close();
        break;

      default: {
        const num = parseInt(key);
        if (num >= 1 && num <= 9 && num <= options.length && this.typewriterDone) {
          e.preventDefault();
          this.selectedOption = num - 1;
          this.selectCurrentOption();
        }
        break;
      }
    }
  }

  // ---- Helpers ----

  private escapeHTML(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
